/**
 * EFL post-login redirect: /account → /pages/b2b-home.
 *
 * The b2b-login form already passes return_to=/pages/b2b-home, so successful
 * sign-ins land on the dashboard directly. This IIFE catches the other entry
 * points: customers who type /account into the URL bar or follow a saved
 * bookmark.
 *
 * Detection: window.__st.cid is the Shopify customer ID, set by analytics
 * very early in the page lifecycle. Two attempts (immediate + 120ms) cover
 * the rare case where Shopify analytics loads after our script.
 *
 * Non-B2B logged-in users (rare in EFL's wholesale store) get redirected
 * here, then b2b-home's own gate bounces them to /pages/b2b-login. That's
 * a 2-hop edge case; acceptable trade-off for the simpler detector.
 */
(function () {
  'use strict';
  if (window.location.pathname !== '/account') return;
  function go() {
    if (window.__st && window.__st.cid) {
      window.location.replace('/pages/b2b-home');
    }
  }
  go();
  setTimeout(go, 120);
})();

/**
 * EFL Header Utilities
 * Injects Quick Order, Wishlist, and Trade Application links into the header icon bar.
 * Also shows/hides Trade Application based on login state.
 */
(function () {
  'use strict';

  const LINKS = [
    {
      id: 'efl-quick-order',
      href: '/pages/quick-order',
      label: 'Quick Order',
      svg: '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/></svg>',
      loggedInOnly: true,
    },
    {
      id: 'efl-wishlist-link',
      href: '/pages/wishlist',
      label: 'Wishlist',
      svg: '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
      loggedInOnly: false,
      badge: true,
    },
    {
      id: 'efl-trade-app',
      href: '/pages/trade-application',
      label: 'Trade Application',
      svg: '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>',
      loggedInOnly: false,
      loggedOutOnly: true,
    },
  ];

  function getWishlistCount() {
    try {
      var wl = JSON.parse(localStorage.getItem('efl_wishlist') || '[]');
      return wl.length;
    } catch (e) {
      return 0;
    }
  }

  function isLoggedIn() {
    // Reliable signals: customer ID set by Shopify analytics, or the FoxEcom
    // account fallback rendered only inside `{% if customer %}`. (Dropped
    // `cart-has-items` — that's set for any visitor with items in cart, even
    // logged-out ones.)
    return (window.__st && window.__st.cid) ||
      document.querySelector('header-account .account-button__fallback') !== null;
  }

  function init() {
    var container = document.querySelector('.header__buttons');
    if (!container) return;

    var searchBtn = container.querySelector('.search-drawer-button');
    var loggedIn = isLoggedIn();

    LINKS.forEach(function (link) {
      if (link.loggedInOnly && !loggedIn) return;
      if (link.loggedOutOnly && loggedIn) return;

      var el = document.createElement('a');
      el.id = link.id;
      el.href = link.href;
      el.className = 'efl-utility-link flex items-center justify-center';
      el.setAttribute('aria-label', link.label);
      el.setAttribute('title', link.label);
      el.innerHTML = link.svg;

      if (link.badge) {
        var count = getWishlistCount();
        if (count > 0) {
          var badge = document.createElement('span');
          badge.className = 'efl-utility-badge';
          badge.textContent = count;
          el.style.position = 'relative';
          el.appendChild(badge);
        }
      }

      if (searchBtn) {
        container.insertBefore(el, searchBtn);
      } else {
        container.insertBefore(el, container.firstChild);
      }
    });

    // Hijack the header account icon. Shopify's <shopify-account> binds its
    // own click handler; we need to win the click race so the icon routes to
    // our B2B pages instead of opening the theme's account dropdown.
    //
    //   1. Visual layer: transparent <a> overlay on <header-account> with a
    //      huge z-index. Most clicks land on the overlay → normal anchor nav.
    //   2. Reliability layer: document-level capture click handler. Capture
    //      phase on `document` fires BEFORE any element-level handler, even
    //      ones inside web-component shadow DOM, so this wins regardless of
    //      who else bound a click on the host.
    //
    // Destination by login state:
    //   logged in  → /pages/b2b-home   (the B2B dashboard)
    //   logged out → /pages/b2b-login  (sign-in + apply page)
    var ACCOUNT_DEST = loggedIn ? '/pages/b2b-home' : '/pages/b2b-login';
    var ACCOUNT_LABEL = loggedIn ? 'B2B account home' : 'Sign in';

    var accountHost = document.querySelector('header-account');
    if (accountHost) {
      if (window.getComputedStyle(accountHost).position === 'static') {
        accountHost.style.position = 'relative';
      }
      var overlay = document.createElement('a');
      overlay.id = 'efl-account-overlay';
      overlay.href = ACCOUNT_DEST;
      overlay.setAttribute('aria-label', ACCOUNT_LABEL);
      overlay.style.cssText = 'position:absolute;inset:0;z-index:9999;display:block;cursor:pointer;background:transparent;';
      accountHost.appendChild(overlay);
    }

    // Document-level capture: catches clicks anywhere inside <header-account>
    // before <shopify-account> gets them. This is the reliable path even if
    // the theme re-mounts widgets later in the page lifecycle and pushes our
    // overlay out of the click target.
    //
    // Carve-out: real <a href> links inside the dropdown (Orders, Profile,
    // etc rendered by <shopify-account>) need to navigate to their own href,
    // not be redirected to ACCOUNT_DEST.
    document.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;
      var host = t.closest('header-account');
      if (!host) return;
      // Allow clicks on our own overlay to navigate normally.
      if (t.id === 'efl-account-overlay' || (t.closest && t.closest('#efl-account-overlay'))) return;
      // Allow real anchor links inside the dropdown to navigate normally.
      if (t.closest('a[href]')) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      ev.stopPropagation();
      window.location.href = ACCOUNT_DEST;
    }, true);

    // Hide the legacy /pages/b2b-login or /pages/b2b-home menu/footer links
    // by login state (defensive — those links aren't currently in the menu,
    // but if anyone re-adds them we don't want to show the wrong one).
    if (loggedIn) {
      document.body.classList.add('efl-customer-logged-in');
      document.querySelectorAll('a[href$="/pages/b2b-login"]').forEach(function (a) {
        if (a.id === 'efl-account-overlay') return;
        var li = a.closest('li');
        (li || a).style.display = 'none';
      });
    } else {
      document.querySelectorAll('a[href$="/pages/b2b-home"]').forEach(function (a) {
        if (a.id === 'efl-account-overlay') return;
        var li = a.closest('li');
        (li || a).style.display = 'none';
      });
    }
  }

  // Inject styles
  var style = document.createElement('style');
  style.textContent = [
    '.efl-utility-link {',
    '  width: var(--header-icon-size);',
    '  height: var(--header-icon-size);',
    '  color: inherit;',
    '  opacity: 0.85;',
    '  transition: opacity 0.2s;',
    '}',
    '.efl-utility-link:hover { opacity: 1; }',
    '.efl-utility-badge {',
    '  position: absolute;',
    '  top: 2px; right: 0;',
    '  background: var(--color-button, #b8860b);',
    '  color: #fff;',
    '  font-size: 10px;',
    '  line-height: 1;',
    '  padding: 2px 5px;',
    '  border-radius: 10px;',
    '  font-weight: 700;',
    '}',
    '@media (max-width: 639.98px) {',
    '  #efl-quick-order, #efl-trade-app { display: none; }',
    '}',
    '.efl-customer-logged-in a[href$="/pages/b2b-login"] { display: none !important; }',
    '.efl-customer-logged-in li:has(> a[href$="/pages/b2b-login"]) { display: none !important; }',
    'body:not(.efl-customer-logged-in) a[href$="/pages/b2b-home"] { display: none !important; }',
    'body:not(.efl-customer-logged-in) li:has(> a[href$="/pages/b2b-home"]) { display: none !important; }',
  ].join('\n');
  document.head.appendChild(style);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * EFL mega-menu hover smoother.
 *
 * The FoxEcom theme closes mega menus the instant the cursor leaves the
 * <details>. With slide animations on both open and close, this makes
 * boundary wobbles look like the panel is popping up and down.
 *
 * Strategy: take over close timing entirely.
 *   1. Capture mouseleave on each <details is="details-mega"> in the
 *      capture phase and call stopImmediatePropagation() — the theme's
 *      bubble-phase mouseleave listener never runs, so no instant close.
 *   2. Schedule our own close after a grace window. If the cursor comes
 *      back to this menu OR moves to a sibling mega, we cancel the close.
 *   3. Block re-opens for a short cooldown after we close, so an
 *      immediate cursor wiggle doesn't restart the slide-in animation
 *      mid-slide-out (which is what causes the "laggy" feel).
 *
 * The CSS rule `details[is="details-mega"][open] > .mega-menu { pointer-events: auto }`
 * (in theme.css) handles the separate 100 ms dead-zone the theme JS
 * introduces while the panel transitions in. Both fixes are needed.
 */
(function () {
  'use strict';

  const CLOSE_GRACE_MS = 350;       // how long after mouseleave before we close
  const REOPEN_COOLDOWN_MS = 200;   // suppress re-open within this window after close
  const closeTimers = new WeakMap();
  const lastClosedAt = new WeakMap();

  function cancelTimer(el) {
    const t = closeTimers.get(el);
    if (t) { clearTimeout(t); closeTimers.delete(el); }
  }

  function attach(el) {
    if (el.dataset.eflSmoothAttached) return;
    el.dataset.eflSmoothAttached = '1';

    // Capture-phase mouseenter: cancel pending close + close other megas.
    // We do NOT stopImmediatePropagation here — let the theme open the panel.
    el.addEventListener('mouseenter', () => {
      if (el.getAttribute('trigger') !== 'hover') return;
      cancelTimer(el);
      document.querySelectorAll('details[is="details-mega"][open]').forEach((other) => {
        if (other === el) return;
        cancelTimer(other);
        other.open = false;
      });
    }, true);

    // Capture-phase mouseleave: SHUT DOWN the theme's bubble-phase listener
    // (which would set open=false instantly) and run our delayed close instead.
    el.addEventListener('mouseleave', (event) => {
      if (el.getAttribute('trigger') !== 'hover') return;
      // Block the theme JS's mouseleave from firing — its handler is on the
      // same element in bubble phase, so stopImmediatePropagation in our
      // capture-phase listener prevents it from running.
      event.stopImmediatePropagation();
      if (!el.open) return;
      cancelTimer(el);
      const timer = setTimeout(() => {
        closeTimers.delete(el);
        if (!el.matches(':hover')) {
          lastClosedAt.set(el, Date.now());
          el.open = false;
        }
      }, CLOSE_GRACE_MS);
      closeTimers.set(el, timer);
    }, true);

    // After we close, briefly suppress re-opens. Without this, a cursor
    // wiggle right at the boundary triggers open → close → open and the
    // slide animations restart on top of each other.
    // We piggy-back on capture-phase mouseenter (above) for the cooldown
    // gate by also intercepting it in a second capture listener that runs
    // before the theme's bubble-phase open=true.
    el.addEventListener('mouseenter', (event) => {
      if (el.getAttribute('trigger') !== 'hover') return;
      const closedAt = lastClosedAt.get(el) || 0;
      if (Date.now() - closedAt < REOPEN_COOLDOWN_MS) {
        event.stopImmediatePropagation();
      }
    }, true);
  }

  function init() {
    document.querySelectorAll('details[is="details-mega"]').forEach(attach);
    new MutationObserver((muts) => {
      muts.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches('details[is="details-mega"]')) attach(n);
          n.querySelectorAll && n.querySelectorAll('details[is="details-mega"]').forEach(attach);
        });
      });
    }).observe(document.body || document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ─── PLP filter reachability (2026-06-12) ───────────────────────────────
   Endless scroll pushes the facets out of reach. Two fixes:
   1. Desktop: pin the facets sidebar (.collection__content .sticky-element)
      with its own internal scroll, offset below the sticky header.
   2. Mobile/tablet: floating "Filter & Sort" pill (appears after 600px of
      scroll) that opens the theme's existing facets drawer. */
(function () {
  'use strict';
  var drawerBtn = document.querySelector('.facet-drawer-button');
  var facetsCol = document.querySelector('.collection__content .sticky-element');
  if (!drawerBtn && !facetsCol) return; // not a filterable PLP

  var css = [
    '@media (min-width: 1280px) {',
    '  .collection__content .hidden .sticky-element {',           // facets SIDEBAR only
    '    position: sticky !important;',
    '    top: calc(var(--header-height, 90px) + 16px);',
    '    max-height: calc(100vh - var(--header-height, 90px) - 32px);',
    '    overflow-y: auto;',
    '    overscroll-behavior: contain;',
    '    scrollbar-width: thin;',
    '    scrollbar-color: #d9cdbb transparent;',
    '    padding-right: 10px;',
    '  }',
    '  .collection__content .hidden .sticky-element::-webkit-scrollbar { width: 5px; }',
    '  .collection__content .hidden .sticky-element::-webkit-scrollbar-track { background: transparent; }',
    '  .collection__content .hidden .sticky-element::-webkit-scrollbar-thumb { background: #d9cdbb; border-radius: 999px; }',
    '  .collection__content .hidden .sticky-element:hover::-webkit-scrollbar-thumb { background: #bd945b; }',
    '}',
    '/* collection toolbar: not sticky — scrolls away with the page; filter',
    '   access while scrolling = sticky rail (desktop) / floating pill (mobile) */',
    '.collection__content .show-toolbar > .sticky-element {',
    '  position: static !important;',
    '  background: transparent;',
    '  border: 0;',
    '  box-shadow: none;',
    '}',
    '.efl-filter-fab {',
    '  position: fixed; left: 50%; bottom: 22px; transform: translateX(-50%) translateY(12px);',
    '  z-index: 60; display: inline-flex; align-items: center; gap: 8px;',
    '  height: 44px; padding: 0 20px; border: 0; border-radius: 999px;',
    '  background: #181209; color: #faf6ef; font-size: 13px; font-weight: 600;',
    '  letter-spacing: .06em; text-transform: uppercase; cursor: pointer;',
    '  box-shadow: 0 12px 40px rgba(24,18,9,.28); opacity: 0; pointer-events: none;',
    '  transition: opacity .2s ease, transform .2s ease;',
    '}',
    '.efl-filter-fab.is-visible { opacity: 1; pointer-events: auto; transform: translateX(-50%) translateY(0); }',
    '@media (min-width: 1280px) { .efl-filter-fab { display: none; } }',
  ].join('\n');
  var style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  // The theme renders the facet drawer button asynchronously — resolve it
  // lazily so the FAB works regardless of load order.
  var fab = document.createElement('button');
  fab.type = 'button';
  fab.className = 'efl-filter-fab';
  fab.innerHTML =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/></svg>Filter &amp; Sort';
  fab.addEventListener('click', function () {
    var btn = document.querySelector('.facet-drawer-button');
    if (btn) btn.click();
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  document.body.appendChild(fab);
  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      fab.classList.toggle('is-visible', window.scrollY > 600);
      ticking = false;
    });
  }, { passive: true });

})();

/* ─── Stock-level range filter (2026-06-12) ──────────────────────────────
   B2B buyers order in quantity — a slider like the price filter that hides
   cards with less than N units in stock. Reads each card's rendered
   `.pcard-stock__qty`; re-applies as endless scroll loads more cards. */
(function () {
  'use strict';
  var form = document.querySelector('.collection__content .hidden .sticky-element .face-form') ||
             document.querySelector('.collection__content .hidden .sticky-element');
  var grid = document.querySelector('.collection__content .products-list, .collection__content [class*=grid-list]');
  if (!form || !grid) return;

  var css = [
    '.efl-qtyfacet { border-top: 1px solid #e6ddd0; padding: 14px 0; }',
    '.efl-qtyfacet summary { list-style: none; cursor: pointer; display: flex; justify-content: space-between; align-items: center; font-size: 15px; color: #181209; }',
    '.efl-qtyfacet summary::-webkit-details-marker { display: none; }',
    '.efl-qtyfacet summary::after { content: "+"; font-size: 17px; color: #3b3128; }',
    '.efl-qtyfacet[open] summary::after { content: "\\2212"; }',
    '.efl-qtyfacet__body { padding-top: 12px; }',
    '.efl-qtyfacet__label { font-size: 13px; color: #3b3128; display: flex; justify-content: space-between; margin-bottom: 8px; }',
    '.efl-qtyfacet__label strong { color: #181209; }',
    '.efl-qtyfacet input[type=range] { width: 100%; appearance: none; -webkit-appearance: none; height: 3px; border-radius: 999px; background: #e6ddd0; outline: none; }',
    '.efl-qtyfacet input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 999px; background: #181209; border: 2px solid #faf6ef; box-shadow: 0 1px 4px rgba(24,18,9,.3); cursor: pointer; }',
    '.efl-qtyfacet input[type=range]::-moz-range-thumb { width: 18px; height: 18px; border-radius: 999px; background: #181209; border: 2px solid #faf6ef; cursor: pointer; }',
    '.efl-qtyfacet__note { font-size: 12px; color: #a5a4ab; margin-top: 8px; }',
  ].join('\n');
  var style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  var STEPS = [0, 1, 5, 10, 25, 50, 100, 200];
  var facet = document.createElement('details');
  facet.className = 'efl-qtyfacet';
  facet.innerHTML =
    '<summary>Stock Level</summary>' +
    '<div class="efl-qtyfacet__body">' +
    '  <div class="efl-qtyfacet__label"><span>At least</span><strong data-qty-readout>Any</strong></div>' +
    '  <input type="range" min="0" max="' + (STEPS.length - 1) + '" step="1" value="0" aria-label="Minimum stock">' +
    '  <p class="efl-qtyfacet__note" data-qty-note></p>' +
    '</div>';
  form.appendChild(facet);

  var slider = facet.querySelector('input');
  var readout = facet.querySelector('[data-qty-readout]');
  var note = facet.querySelector('[data-qty-note]');

  function cardQty(card) {
    var el = card.querySelector('.pcard-stock__qty');
    var n = el ? parseInt(el.textContent.replace(/[^0-9]/g, ''), 10) : NaN;
    return isNaN(n) ? 0 : n;
  }
  function items() {
    return [...grid.querySelectorAll('.product-card')].map(function (c) {
      return { card: c, host: c.closest('li, .f-column') || c };
    });
  }
  function apply() {
    var min = STEPS[+slider.value];
    readout.textContent = min === 0 ? 'Any' : min + '+ in stock';
    var shown = 0, total = 0;
    items().forEach(function (it) {
      total++;
      var ok = min === 0 || cardQty(it.card) >= min;
      it.host.style.display = ok ? '' : 'none';
      if (ok) shown++;
    });
    note.textContent = min === 0 ? '' : 'Showing ' + shown + ' of ' + total + ' loaded items';
  }
  slider.addEventListener('input', apply);

  // endless scroll loads more cards — keep the filter applied
  new MutationObserver(function () {
    if (+slider.value > 0) apply();
  }).observe(grid, { childList: true, subtree: true });
})();

// Base theme's facets.js calls gridList.reload() after loading more products,
// but GridList never defines it — reveal the newly appended cards instead.
(function () {
  if (!window.customElements) return;
  customElements.whenDefined('grid-list').then(function () {
    var proto = customElements.get('grid-list').prototype;
    if (typeof proto.reload !== 'function') {
      proto.reload = function () {
        if (typeof this.reShowVisibleGridItems === 'function') this.reShowVisibleGridItems();
      };
    }
  });
})();
